import type { FC, ReactNode } from 'react'

import { useEffect, useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { ChevronRight, Menu, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { ButtonLink } from '@/components/generals/button-link'
import { Container } from '@/components/generals/container'
import { Logo } from '@/components/generals/logo'

import { NAV_LINKS, siteConfig } from '@/constants/site'

export const SiteHeader: FC = (): ReactNode => {
    const router = useRouter()
    const [open, setOpen] = useState(false)

    useEffect(() => {
        const close = (): void => setOpen(false)

        router.events.on('routeChangeStart', close)
        router.events.on('hashChangeStart', close)

        return () => {
            router.events.off('routeChangeStart', close)
            router.events.off('hashChangeStart', close)
        }
    }, [router.events])

    useEffect(() => {
        if (!open) return

        const onKeyDown = (event: KeyboardEvent): void => {
            if (event.key === 'Escape') setOpen(false)
        }

        window.addEventListener('keydown', onKeyDown)

        return () => window.removeEventListener('keydown', onKeyDown)
    }, [open])

    const isActive = (href: string): boolean => href === '/courses' && router.pathname.startsWith('/courses')

    return (
        <header className='sticky top-0 z-40 border-b border-border/70 bg-background/85 backdrop-blur-md'>
            <Container className='flex h-16 items-center justify-between gap-6'>
                <Link
                    href='/'
                    aria-label={`${siteConfig.name} home`}
                    className='rounded-lg focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ring'
                >
                    <Logo />
                </Link>

                <nav aria-label='Main' className='hidden md:block'>
                    <ul className='flex items-center gap-1'>
                        {NAV_LINKS.map(link => (
                            <li key={link.href}>
                                <Link
                                    href={link.href}
                                    aria-current={isActive(link.href) ? 'page' : undefined}
                                    className={cn(
                                        'rounded-full px-3.5 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-2 focus-visible:outline-ring',
                                        isActive(link.href) && 'bg-muted text-foreground'
                                    )}
                                >
                                    {link.label}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </nav>

                <div className='flex items-center gap-2'>
                    <ButtonLink href='/enroll' size='sm' className='hidden sm:inline-flex'>
                        Enroll now
                    </ButtonLink>

                    <button
                        type='button'
                        onClick={() => setOpen(current => !current)}
                        aria-expanded={open}
                        aria-controls='mobile-menu'
                        className='inline-flex size-10 items-center justify-center rounded-full border border-foreground/15 bg-card text-foreground transition-colors hover:bg-muted focus-visible:outline-2 focus-visible:outline-ring md:hidden'
                    >
                        {open ? <X className='size-5' aria-hidden='true' /> : <Menu className='size-5' aria-hidden='true' />}

                        <span className='sr-only'>
                            {open ? 'Close menu' : 'Open menu'}
                        </span>
                    </button>
                </div>
            </Container>

            <div id='mobile-menu' hidden={!open} className='border-t bg-background md:hidden'>
                <Container className='py-4'>
                    <nav aria-label='Mobile'>
                        <ul className='flex flex-col'>
                            {NAV_LINKS.map(link => (
                                <li key={link.href}>
                                    <Link
                                        href={link.href}
                                        onClick={() => setOpen(false)}
                                        className='flex items-center justify-between rounded-xl px-3 py-3 text-base font-medium text-foreground transition-colors hover:bg-muted'
                                    >
                                        {link.label}
                                        <ChevronRight className='size-4 text-muted-foreground' aria-hidden='true' />
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </nav>

                    <ButtonLink href='/enroll' onClick={() => setOpen(false)} className='mt-3 w-full'>
                        Enroll now
                    </ButtonLink>
                </Container>
            </div>
        </header>
    )
}
